import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DivisionConfig, DivisionConfigData } from '@r1-compass-library/models/division/division-config.model';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { catchError, finalize, map, Observable, of, retry } from 'rxjs';
import { SpinnerService } from './spinner.service';
import { Environment } from '@r1-compass-library/environments/environment';
import { DivisionBasicSettings, DivisionSalesforceTargetSettings } from '@r1-compass-library/models';

@Injectable({ providedIn: 'root' })
export class DivisionConfigService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private BASE_URL: string = Environment.baseUrl + '/frontend/division-configs';

    get(id: number): Observable<DivisionConfig> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'DivisionConfigService.get';
        this.#spinnerService.addProcess(process, 'Getting division config...');
        return this.#http.get<DivisionConfig>(`${this.BASE_URL}/${id}`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DivisionConfig>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getByIamDivisionId(iamDivisionId: number): Observable<DivisionConfig> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams().set('iamDivisionId', iamDivisionId),
        };

        const process = 'DivisionConfigService.getByIamDivisionId';
        this.#spinnerService.addProcess(process, 'Getting division config...');
        return this.#http.get<DivisionConfig[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            map(configs => configs[0]),
            catchError(() => {
                return of<DivisionConfig>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    create(
        divisionConfig:
            | (Omit<Partial<DivisionConfig>, 'config'> & { config: { mapping: DivisionBasicSettings } }) // updating a division setting
            | Partial<DivisionConfig> // creating a division
    ): Observable<DivisionConfig> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };

        const process = 'DivisionConfigService.create';
        this.#spinnerService.addProcess(process, 'Creating division config...');
        return this.#http.post<DivisionConfig>(`${this.BASE_URL}`, divisionConfig, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<DivisionConfig>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    update(id: number, divisionConfig: Partial<DivisionConfig>): Observable<DivisionConfig> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
            }),
        };

        const process = 'DivisionConfigService.update';
        this.#spinnerService.addProcess(process, 'Updating division config...');
        return this.#http.patch<DivisionConfig>(`${this.BASE_URL}/${id}`, divisionConfig, httpOptions).pipe(
            catchError(error => {
                return of<DivisionConfig>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    /**
     * @description This patch data endpoint is used for updating only a part of the overall config blob without losing other data - Steve
     * {@link} https://relationship-one.slack.com/archives/C05U2241VS4/p1776965426920229?thread_ts=1776965139.365689&cid=C05U2241VS4
     */
    updateData(
        id: number,
        data:
            | Partial<DivisionConfigData>
            | {
                  mapping: DivisionBasicSettings;
              }
            | { mapping: DivisionBasicSettings & DivisionSalesforceTargetSettings }
    ): Observable<DivisionConfig> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
            }),
        };

        const process = 'DivisionConfigService.updateData';
        this.#spinnerService.addProcess(process, 'Updating division config...');
        return this.#http.patch<DivisionConfig>(`${this.BASE_URL}/${id}/data?fieldName=config`, data, httpOptions).pipe(
            catchError(error => {
                return of<DivisionConfig>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    findAll(): Observable<DivisionConfig[]> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };

        const process = 'DivisionConfigService.findAll';
        this.#spinnerService.addProcess(process, 'Getting division configs...');
        return this.#http.get<DivisionConfig[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<DivisionConfig[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }
}
